import React, { useState } from 'react';
import { useCart } from '../context/CartContext';
import '../stylings/BucksBasket.css';

function BucksBasket() {
  const { 
    cartItems, 
    removeFromCart, 
    updateQuantity, 
    clearCart,
    subtotal,
    calculateTotal,
    isCartLoaded  // Add this line
  } = useCart();
  
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(false);
  const [shippingMethod, setShippingMethod] = useState('delivery');
  
  // Shipping fee calculation
  const shippingFee = subtotal > 50 ? 0 : 8.99;
  
  // Apply promo code
  const applyPromoCode = (e) => {
    e.preventDefault();
    // In a real app, you'd validate the promo code with an API
    if (promoCode.toLowerCase() === 'harvest') {
      setPromoApplied(true);
    } else {
      alert('Invalid promo code. Try "HARVEST" for this demo.');
    }
  };
  
  // Handle quantity change
  const handleQuantityChange = (id, newQty) => {
    updateQuantity(id, parseInt(newQty));
  };
  
  // Handle checkout navigation
  const handleCheckout = () => {
    window.location.href = '/checkout';
  };
  
  // Check if cart is empty - but only after it's loaded
  const isCartEmpty = cartItems.length === 0;
  const showEmptyState = isCartLoaded && isCartEmpty;
  const showLoadingState = !isCartLoaded;

  return (
    <div className="bucks-basket">
      <div className="basket-container">
        <div className="basket-header">
          <h1>Your Harvest Basket</h1>
          <p className="basket-subtitle">Review your selected farm-fresh items</p>
        </div>
        
        {showLoadingState ? (
          <div className="basket-loading">
            <div className="loading-spinner"></div>
            <h2>Loading your basket...</h2>
            <p>Please wait while we retrieve your items.</p>
          </div>
        ) : showEmptyState ? (
          <div className="empty-basket">
            <div className="empty-basket-icon">🧺</div>
            <h2>Your basket is empty</h2>
            <p>Looks like you haven't added any farm-fresh goodies to your basket yet.</p>
            <a href="/shop" className="cta-primary">Browse Our Harvest</a>
          </div>
        ) : (
          <div className="basket-content">
            <div className="basket-items-section">
              <div className="basket-column-headers">
                <span className="column-header product-column">Product</span>
                <span className="column-header price-column">Price</span>
                <span className="column-header quantity-column">Quantity</span>
                <span className="column-header total-column">Total</span>
              </div>
              
              <ul className="basket-items">
                {cartItems.map((item) => (
                  <li key={item.id} className="basket-item">
                    <div className="item-info">
                      <div className="item-image-container">
                        <img src={item.image} alt={item.name} className="item-image" />
                      </div>
                      <div className="item-details">
                        <h3 className="item-name">{item.name}</h3>
                        {item.category && <span className="item-category">{item.category}</span>}
                      </div>
                    </div>
                    
                    <div className="item-price">${item.price.toFixed(2)}</div>
                    
                    <div className="item-quantity">
                      <div className="quantity-controls">
                        <button 
                          className="quantity-btn" 
                          onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                          aria-label="Decrease quantity"
                        >
                          −
                        </button>
                        <input
                          type="number"
                          value={item.quantity}
                          min="1"
                          onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                          className="quantity-input"
                        />
                        <button 
                          className="quantity-btn" 
                          onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                          aria-label="Increase quantity"
                        >
                          +
                        </button>
                      </div>
                    </div>
                    
                    <div className="item-total">
                      ${(item.price * item.quantity).toFixed(2)}
                      <button 
                        className="remove-btn" 
                        onClick={() => removeFromCart(item.id)}
                        aria-label="Remove item"
                      >
                        ×
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
              
              <div className="basket-actions">
                <button onClick={clearCart} className="clear-basket-btn">
                  Clear Basket
                </button>
                <a href="/shop" className="continue-shopping-btn">
                  Continue Shopping
                </a>
              </div>
            </div>
            
            <div className="basket-summary">
              <h2>Basket Summary</h2>
              
              <div className="promo-section">
                <form onSubmit={applyPromoCode} className="promo-form">
                  <input
                    type="text"
                    placeholder="Enter promo code"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value)}
                    className="promo-input"
                    disabled={promoApplied}
                  />
                  <button 
                    type="submit" 
                    className="promo-btn"
                    disabled={promoApplied}
                  >
                    {promoApplied ? 'Applied' : 'Apply'}
                  </button>
                </form>
                {promoApplied && (
                  <div className="promo-success">
                    <span>✓</span> HARVEST promo code applied!
                  </div>
                )}
              </div>
              
              <div className="shipping-section">
                <h3>Delivery Options</h3>
                <div className="shipping-options">
                  <label className="shipping-option">
                    <input
                      type="radio"
                      name="shipping"
                      value="delivery"
                      checked={shippingMethod === 'delivery'}
                      onChange={() => setShippingMethod('delivery')}
                    />
                    <div className="option-details">
                      <span className="option-name">Home Delivery</span>
                      <span className="option-price">
                        {shippingFee === 0 
                          ? 'Free (orders over $50)' 
                          : `$${shippingFee.toFixed(2)}`
                        }
                      </span>
                    </div>
                  </label>
                  
                  <label className="shipping-option">
                    <input
                      type="radio"
                      name="shipping"
                      value="pickup"
                      checked={shippingMethod === 'pickup'}
                      onChange={() => setShippingMethod('pickup')}
                    />
                    <div className="option-details">
                      <span className="option-name">Farmstand Pickup</span>
                      <span className="option-price">Free</span>
                    </div>
                  </label>
                </div>
              </div>
              
              <div className="summary-details">
                <div className="summary-line">
                  <span>Subtotal</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                
                {promoApplied && (
                  <div className="summary-line discount">
                    <span>Discount (10%)</span>
                    <span>-${(subtotal * 0.10).toFixed(2)}</span>
                  </div>
                )}
                
                {shippingMethod === 'delivery' && (
                  <div className="summary-line">
                    <span>Shipping</span>
                    <span>
                      {shippingFee === 0 
                        ? 'Free' 
                        : `$${shippingFee.toFixed(2)}`
                      }
                    </span>
                  </div>
                )}
                
                <div className="summary-line total">
                  <span>Total</span>
                  <span>
                    ${promoApplied 
                      ? (subtotal * 0.9 + (shippingMethod === 'delivery' ? shippingFee : 0)).toFixed(2)
                      : (subtotal + (shippingMethod === 'delivery' ? shippingFee : 0)).toFixed(2)
                    }
                  </span>
                </div>
              </div>
              
              <button 
                className="checkout-btn"
                onClick={handleCheckout}
                disabled={false}
              >
                Proceed to Checkout
              </button>
              
              <div className="payment-icons">
                <span className="payment-icon">💳</span>
                <span className="payment-text">Secure Payment</span>
              </div>
              
              <div className="basket-trust-messages">
                <div className="trust-message">
                  <span className="trust-icon">🌱</span>
                  <span>Farm Fresh Guarantee</span>
                </div>
                <div className="trust-message">
                  <span className="trust-icon">🚚</span>
                  <span>{shippingFee === 0 ? 'Free delivery on orders over $50' : 'Same-day local delivery available'}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default BucksBasket;